import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Save, 
  X, 
  Upload, 
  MapPin, 
  Home,
  DollarSign,
  Image as ImageIcon,
  Video,
  Building,
  ArrowLeft
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_CPF = '00000000000';

const TIPOS_IMOVEL = [
  { value: 'venda', label: 'Venda' },
  { value: 'aluguel', label: 'Aluguel' },
  { value: 'arrendamento', label: 'Arrendamento' }
];

const STATUS_IMOVEL = [
  { value: 'ativo', label: 'Ativo' },
  { value: 'inativo', label: 'Inativo' },
  { value: 'vendido', label: 'Vendido' },
  { value: 'alugado', label: 'Alugado' },
  { value: 'arrendado', label: 'Arrendado' }
];

const AdminImoveis = ({ user }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [imoveis, setImoveis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editando, setEditando] = useState(null);
  const [criando, setCriando] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(null);
  
  // Formulário
  const [formData, setFormData] = useState({
    tipo: 'venda',
    descricao_imovel: '',
    localidade: '',
    municipio: '',
    descricao_geral: '',
    area: '',
    valor: '',
    localizacao_lat: '',
    localizacao_lng: '',
    localizacao_endereco: '',
    link_video: '',
    status: 'ativo'
  });

  // Verifica se é admin
  const isAdmin = user?.cpf?.replace(/\D/g, '') === ADMIN_CPF;

  useEffect(() => {
    if (!isAdmin) {
      toast({
        title: "Acesso negado",
        description: "Você não tem permissão para acessar esta área.",
        variant: "destructive",
      });
      navigate('/');
      return;
    }
    carregarImoveis();
  }, [isAdmin, navigate, toast]);

  const carregarImoveis = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/imoveis`);
      if (response.data.success) {
        setImoveis(response.data.imoveis);
      }
    } catch (error) {
      console.error('Erro ao carregar imóveis:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os imóveis.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      tipo: 'venda',
      descricao_imovel: '',
      localidade: '',
      municipio: '',
      descricao_geral: '',
      area: '',
      valor: '',
      localizacao_lat: '',
      localizacao_lng: '',
      localizacao_endereco: '',
      link_video: '',
      status: 'ativo'
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCriar = async () => {
    try {
      const payload = {
        ...formData,
        valor: parseFloat(formData.valor) || 0
      };
      
      const response = await axios.post(`${API_URL}/api/imoveis`, payload);
      
      if (response.data.success) {
        toast({
          title: "Sucesso!",
          description: "Imóvel criado com sucesso.",
        });
        setCriando(false);
        resetForm();
        carregarImoveis();
      }
    } catch (error) {
      console.error('Erro ao criar imóvel:', error);
      toast({
        title: "Erro",
        description: error.response?.data?.detail || "Não foi possível criar o imóvel.",
        variant: "destructive",
      });
    }
  };

  const handleEditar = (imovel) => {
    setEditando(imovel.id);
    setFormData({
      tipo: imovel.tipo || 'venda',
      descricao_imovel: imovel.descricao_imovel || '',
      localidade: imovel.localidade || '',
      municipio: imovel.municipio || '',
      descricao_geral: imovel.descricao_geral || '',
      area: imovel.area || '',
      valor: imovel.valor?.toString() || '',
      localizacao_lat: imovel.localizacao_lat || '',
      localizacao_lng: imovel.localizacao_lng || '',
      localizacao_endereco: imovel.localizacao_endereco || '',
      link_video: imovel.link_video || '',
      status: imovel.status || 'ativo'
    });
  };

  const handleSalvarEdicao = async () => {
    try {
      const payload = {
        ...formData,
        valor: parseFloat(formData.valor) || 0
      };
      
      const response = await axios.put(`${API_URL}/api/imoveis/${editando}`, payload);
      
      if (response.data.success) {
        toast({
          title: "Sucesso!",
          description: "Imóvel atualizado com sucesso.",
        });
        setEditando(null);
        resetForm();
        carregarImoveis();
      }
    } catch (error) {
      console.error('Erro ao atualizar imóvel:', error);
      toast({
        title: "Erro",
        description: error.response?.data?.detail || "Não foi possível atualizar o imóvel.",
        variant: "destructive",
      });
    }
  };

  const handleDeletar = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este imóvel? Esta ação não pode ser desfeita.')) {
      return;
    }
    
    try {
      const response = await axios.delete(`${API_URL}/api/imoveis/${id}`);
      
      if (response.data.success) {
        toast({
          title: "Sucesso!",
          description: "Imóvel excluído com sucesso.",
        });
        carregarImoveis();
      }
    } catch (error) {
      console.error('Erro ao excluir imóvel:', error);
      toast({
        title: "Erro",
        description: error.response?.data?.detail || "Não foi possível excluir o imóvel.",
        variant: "destructive",
      });
    }
  };

  const handleUploadImagem = async (imovelId, event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setUploadingImage(imovelId);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API_URL}/api/imoveis/${imovelId}/imagens`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      if (response.data.success) {
        toast({
          title: "Sucesso!",
          description: `Imagem enviada! Total: ${response.data.total_imagens}/4`,
        });
        carregarImoveis();
      }
    } catch (error) {
      console.error('Erro ao enviar imagem:', error);
      toast({
        title: "Erro",
        description: error.response?.data?.detail || "Não foi possível enviar a imagem.",
        variant: "destructive",
      });
    } finally {
      setUploadingImage(null);
      event.target.value = '';
    }
  };

  const handleDeletarImagem = async (imovelId, url) => {
    if (!window.confirm('Tem certeza que deseja remover esta imagem?')) {
      return;
    }
    
    try {
      const response = await axios.delete(
        `${API_URL}/api/imoveis/${imovelId}/imagens`,
        { params: { url } }
      );
      
      if (response.data.success) {
        toast({
          title: "Sucesso!",
          description: "Imagem removida com sucesso.",
        });
        carregarImoveis();
      }
    } catch (error) {
      console.error('Erro ao remover imagem:', error);
      toast({
        title: "Erro",
        description: error.response?.data?.detail || "Não foi possível remover a imagem.",
        variant: "destructive",
      });
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ativo': return 'bg-green-100 text-green-800';
      case 'inativo': return 'bg-gray-100 text-gray-800';
      case 'vendido': return 'bg-blue-100 text-blue-800';
      case 'alugado': return 'bg-purple-100 text-purple-800';
      case 'arrendado': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTipoLabel = (tipo) => {
    return TIPOS_IMOVEL.find(t => t.value === tipo)?.label || tipo;
  };

  if (!isAdmin) {
    return null;
  }

  // Formulário de criação/edição
  const renderForm = () => (
    <Card className="mb-8">
      <CardHeader className="bg-[rgb(25,42,86)] text-white rounded-t-lg">
        <CardTitle className="flex items-center">
          <Building className="h-5 w-5 mr-2" />
          {criando ? 'Novo Imóvel' : 'Editar Imóvel'}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Tipo */}
          <div>
            <Label htmlFor="tipo">Tipo *</Label>
            <select
              id="tipo"
              name="tipo"
              value={formData.tipo}
              onChange={handleInputChange}
              className="w-full mt-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[rgb(25,42,86)] focus:border-transparent"
            >
              {TIPOS_IMOVEL.map(tipo => (
                <option key={tipo.value} value={tipo.value}>{tipo.label}</option>
              ))}
            </select>
          </div>

          {/* Status */}
          <div>
            <Label htmlFor="status">Status *</Label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              className="w-full mt-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[rgb(25,42,86)] focus:border-transparent"
            >
              {STATUS_IMOVEL.map(status => (
                <option key={status.value} value={status.value}>{status.label}</option>
              ))}
            </select>
          </div>

          {/* Descrição do Imóvel */}
          <div className="md:col-span-2">
            <Label htmlFor="descricao_imovel">Descrição do Imóvel *</Label>
            <Input
              id="descricao_imovel"
              name="descricao_imovel"
              value={formData.descricao_imovel}
              onChange={handleInputChange}
              placeholder="Ex: Fazenda Santa Maria"
              className="mt-1"
            />
          </div>

          {/* Localidade */}
          <div>
            <Label htmlFor="localidade">Localidade *</Label>
            <Input
              id="localidade"
              name="localidade"
              value={formData.localidade}
              onChange={handleInputChange}
              placeholder="Ex: Zona Rural"
              className="mt-1"
            />
          </div>

          {/* Município */}
          <div>
            <Label htmlFor="municipio">Município *</Label>
            <Input
              id="municipio"
              name="municipio"
              value={formData.municipio}
              onChange={handleInputChange}
              placeholder="Ex: Bagé - RS"
              className="mt-1"
            />
          </div>

          {/* Área */}
          <div>
            <Label htmlFor="area">Área (hectares ou m²) *</Label>
            <Input
              id="area"
              name="area"
              value={formData.area}
              onChange={handleInputChange}
              placeholder="Ex: 500 hectares ou 1000 m²"
              className="mt-1"
            />
          </div>

          {/* Valor */}
          <div>
            <Label htmlFor="valor">Valor (R$) *</Label>
            <Input
              id="valor"
              name="valor"
              type="number"
              step="0.01"
              value={formData.valor}
              onChange={handleInputChange}
              placeholder="Ex: 5000000.00"
              className="mt-1"
            />
          </div>

          {/* Descrição Geral */}
          <div className="md:col-span-2">
            <Label htmlFor="descricao_geral">Descrição Geral *</Label>
            <Textarea
              id="descricao_geral"
              name="descricao_geral"
              value={formData.descricao_geral}
              onChange={handleInputChange}
              placeholder="Descreva detalhadamente o imóvel..."
              className="mt-1 min-h-[120px]"
            />
          </div>

          {/* Localização */}
          <div className="md:col-span-2">
            <Label className="flex items-center mb-2">
              <MapPin className="h-4 w-4 mr-1" />
              Localização (Coordenadas)
            </Label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Input
                  name="localizacao_lat"
                  value={formData.localizacao_lat}
                  onChange={handleInputChange}
                  placeholder="Latitude (ex: -31.3289)"
                />
              </div>
              <div>
                <Input
                  name="localizacao_lng"
                  value={formData.localizacao_lng}
                  onChange={handleInputChange}
                  placeholder="Longitude (ex: -54.1069)"
                />
              </div>
              <div>
                <Input
                  name="localizacao_endereco"
                  value={formData.localizacao_endereco}
                  onChange={handleInputChange}
                  placeholder="Endereço descritivo"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Dica: Encontre as coordenadas em Google Maps, clique com botão direito no local e copie as coordenadas.
            </p>
          </div>

          {/* Link do Vídeo */}
          <div className="md:col-span-2">
            <Label htmlFor="link_video" className="flex items-center">
              <Video className="h-4 w-4 mr-1" />
              Link do Vídeo (YouTube, etc)
            </Label>
            <Input
              id="link_video"
              name="link_video"
              value={formData.link_video}
              onChange={handleInputChange}
              placeholder="https://youtube.com/watch?v=..."
              className="mt-1"
            />
          </div>
        </div>

        {/* Botões */}
        <div className="flex justify-end space-x-3 mt-6">
          <Button
            variant="outline"
            onClick={() => {
              setCriando(false);
              setEditando(null);
              resetForm();
            }}
          >
            <X className="h-4 w-4 mr-2" />
            Cancelar
          </Button>
          <Button
            onClick={criando ? handleCriar : handleSalvarEdicao}
            className="bg-[rgb(25,42,86)] hover:bg-[rgb(25,42,86)]/90"
          >
            <Save className="h-4 w-4 mr-2" />
            {criando ? 'Criar Imóvel' : 'Salvar Alterações'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="mb-2 text-gray-600 hover:text-[rgb(25,42,86)]"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar
            </Button>
            <h1 className="text-3xl font-black text-[rgb(25,42,86)]">
              Negócios Imobiliários
            </h1>
            <p className="text-gray-600">Gerencie os imóveis disponíveis</p>
          </div>
          
          {!criando && !editando && (
            <Button
              onClick={() => setCriando(true)}
              className="bg-[rgb(238,29,37)] hover:bg-[rgb(238,29,37)]/90"
            >
              <Plus className="h-4 w-4 mr-2" />
              Novo Imóvel
            </Button>
          )}
        </div>

        {/* Formulário */}
        {(criando || editando) && renderForm()}

        {/* Lista de Imóveis */}
        {!criando && !editando && (
          <div className="space-y-6">
            {loading ? (
              <Card className="p-8 text-center">
                <p className="text-gray-500">Carregando imóveis...</p>
              </Card>
            ) : imoveis.length === 0 ? (
              <Card className="p-8 text-center">
                <Building className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">Nenhum imóvel cadastrado</p>
                <Button
                  onClick={() => setCriando(true)}
                  className="bg-[rgb(25,42,86)] hover:bg-[rgb(25,42,86)]/90"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Cadastrar Primeiro Imóvel
                </Button>
              </Card>
            ) : (
              imoveis.map(imovel => (
                <Card key={imovel.id} className="overflow-hidden">
                  <div className="flex flex-col lg:flex-row">
                    {/* Imagens */}
                    <div className="lg:w-1/3 p-4 bg-gray-50">
                      <div className="grid grid-cols-2 gap-2">
                        {imovel.imagens?.map((img, idx) => (
                          <div key={idx} className="relative group aspect-video">
                            <img
                              src={img}
                              alt={`Imagem ${idx + 1}`}
                              className="w-full h-full object-cover rounded-lg"
                            />
                            <button
                              onClick={() => handleDeletarImagem(imovel.id, img)}
                              className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Remover imagem"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                        
                        {/* Placeholder para adicionar imagens */}
                        {(!imovel.imagens || imovel.imagens.length < 4) && (
                          <label className="aspect-video border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-[rgb(25,42,86)] hover:bg-gray-100 transition-colors">
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => handleUploadImagem(imovel.id, e)}
                              disabled={uploadingImage === imovel.id}
                            />
                            {uploadingImage === imovel.id ? (
                              <span className="text-xs text-gray-500">Enviando...</span>
                            ) : (
                              <>
                                <ImageIcon className="h-6 w-6 text-gray-400" />
                                <span className="text-xs text-gray-500 mt-1">
                                  {imovel.imagens?.length || 0}/4
                                </span>
                              </>
                            )}
                          </label>
                        )}
                      </div>
                    </div>

                    {/* Informações */}
                    <div className="lg:w-2/3 p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(imovel.status)}`}>
                              {STATUS_IMOVEL.find(s => s.value === imovel.status)?.label || imovel.status}
                            </span>
                            <span className="px-2 py-1 bg-[rgb(25,42,86)] text-white rounded-full text-xs font-semibold">
                              {getTipoLabel(imovel.tipo)}
                            </span>
                          </div>
                          <h3 className="text-xl font-bold text-[rgb(25,42,86)]">
                            {imovel.descricao_imovel}
                          </h3>
                        </div>
                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditar(imovel)}
                            className="text-[rgb(25,42,86)] border-[rgb(25,42,86)]"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeletar(imovel.id)}
                            className="text-red-600 border-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                        <div className="flex items-center text-gray-600">
                          <MapPin className="h-4 w-4 mr-2 text-[rgb(238,29,37)]" />
                          {imovel.localidade}, {imovel.municipio}
                        </div>
                        <div className="flex items-center text-gray-600">
                          <Home className="h-4 w-4 mr-2 text-[rgb(238,29,37)]" />
                          {imovel.area}
                        </div>
                        <div className="flex items-center font-bold text-[rgb(25,42,86)]">
                          <DollarSign className="h-4 w-4 mr-2 text-[rgb(238,29,37)]" />
                          {formatCurrency(imovel.valor)}
                        </div>
                        {imovel.link_video && (
                          <div className="flex items-center">
                            <Video className="h-4 w-4 mr-2 text-[rgb(238,29,37)]" />
                            <a 
                              href={imovel.link_video} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline"
                            >
                              Ver vídeo
                            </a>
                          </div>
                        )}
                      </div>

                      <p className="text-gray-600 text-sm line-clamp-3">
                        {imovel.descricao_geral}
                      </p>

                      {(imovel.localizacao_lat && imovel.localizacao_lng) && (
                        <div className="mt-4 pt-4 border-t">
                          <a
                            href={`https://www.google.com/maps?q=${imovel.localizacao_lat},${imovel.localizacao_lng}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 hover:underline flex items-center"
                          >
                            <MapPin className="h-4 w-4 mr-1" />
                            Ver no Google Maps
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminImoveis;
